import { useEffect } from "react";
import { App, Form, Input, Modal, Select, Switch } from "antd";
import { ApiService } from "api/api.service";
import { Resource } from "dtos/stage.dto";
import { OrganisationDto } from "dtos/organisation.dto";
import { useNavigate } from "react-router-dom";
import { CreateTaskBoardTemplatePayload } from "dtos/tasks/task-template.dto";
import { OrgSelector } from "components/Organisation/OrganisationSelector";
import { LuLayoutTemplate, LuBuilding2, LuLayers, LuZap } from "react-icons/lu";
import { storedTokens } from "utils/storedTokens";

interface CreateTemplateModalProps {
  open: boolean;
  onClose: () => void;
}

const RESOURCE_OPTIONS = [
  { label: "Loan", value: Resource.LOAN },
  { label: "Application", value: Resource.APPLICATION },
];

/**
 * Step 1 of the task template creation flow.
 * On success, navigates to the template detail page for groups/tasks to be added.
 */
export const CreateTemplateModal = ({ open, onClose }: CreateTemplateModalProps) => {
  const [form] = Form.useForm<CreateTaskBoardTemplatePayload>();
  const { message } = App.useApp();
  const navigate = useNavigate();

  const orgsQuery = ApiService.organisation.useGetAllOrganisations();
  const createTemplate = ApiService.taskTemplate.useCreate();

  const organisations = orgsQuery.data ?? [];
  const selectedOrg = Form.useWatch("organisation", form);

  // Auto-select the FCDS organisation once orgs are loaded (only if untouched).
  useEffect(() => {
    if (!open || organisations.length === 0) return;
    if (form.getFieldValue("organisation")) return;

    const defaultCode = storedTokens.orgCode.getToken();
    const defaultOrg = organisations.find((org) => org.code === defaultCode);
    if (defaultOrg) form.setFieldValue("organisation", defaultOrg._id);
  }, [open, organisations, form]);

  const handleFinish = async (values: CreateTaskBoardTemplatePayload) => {
    try {
      const res = await createTemplate.mutateAsync(values);
      message.success("Template created");
      form.resetFields();
      onClose();
      navigate(`/dashboard/operations/tasks/${res.result._id}`);
    } catch {
      message.error("Failed to create template");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText="Create & Continue"
      okButtonProps={{ loading: createTemplate.isPending }}
      centered
      destroyOnHidden
      width={560}
      title={null}
      styles={{ body: { paddingTop: 0 } }}
    >
      {/* Custom header */}
      <div className="flex items-start gap-3 border-b border-gray-100 pb-4 pt-1">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <LuLayoutTemplate className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h2 className="m-0 text-base font-semibold text-gray-900">New Task Template</h2>
          <p className="m-0 text-sm text-gray-500">Set up the basics, then add groups and tasks in the next step.</p>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ autoStart: true, resource: Resource.APPLICATION }}
        requiredMark="optional"
        className="pt-5"
      >
        <Form.Item
          name="name"
          label={<span className="text-sm font-medium text-gray-700">Template Name</span>}
          rules={[{ required: true, min: 1, message: "Enter a template name" }]}
        >
          <Input size="large" placeholder="e.g. Debt Resolution Checklist" />
        </Form.Item>

        {/* Organisation + Resource on a single line */}
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item
            name="organisation"
            label={
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <LuBuilding2 className="h-3.5 w-3.5 text-gray-400" />
                Organisation
              </span>
            }
            rules={[{ required: true, message: "Select an organisation" }]}
          >
            {/* Storing _id in the form, OrgSelector supports matching by _id */}
            <OrgSelector
              organisations={organisations}
              selected={selectedOrg}
              onChange={(org: OrganisationDto) => form.setFieldValue("organisation", org._id)}
            />
          </Form.Item>

          <Form.Item
            name="resource"
            label={
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <LuLayers className="h-3.5 w-3.5 text-gray-400" />
                Resource
              </span>
            }
            rules={[{ required: true, message: "Select a resource" }]}
          >
            <Select size="large" options={RESOURCE_OPTIONS} placeholder="Select resource" />
          </Form.Item>
        </div>

        {/* Auto Start as a highlighted toggle row */}
        <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600">
              <LuZap className="h-4 w-4" />
            </div>
            <div>
              <p className="m-0 text-sm font-medium text-gray-800">Auto Start</p>
              <p className="m-0 text-xs text-gray-500">
                Tasks activate automatically as conditions are met — no manual trigger needed.
              </p>
            </div>
          </div>
          <Form.Item name="autoStart" valuePropName="checked" noStyle>
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};
